import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { API_BASE_URL } from '../../../config';
import { legendTooltipFormatter } from './legendTooltip';

const weeklyLoadLegendDefs: Record<string, string> = {
  'Endurance': 'Time in Zone 1-2 (<75% FTP). Aerobic base building.',
  'Tempo': 'Time in Zone 3 (76-90% FTP). Moderate intensity, builds muscular endurance.',
  'Threshold': 'Time in Zone 4 (91-105% FTP). Builds FTP and lactate tolerance.',
  'VO2max': 'Time in Zone 5 (106-120% FTP). Builds maximal aerobic capacity.',
  'Anaerobic': 'Time spent in Zone 6-7 (>120% FTP). Sprint and neuromuscular efforts.',
};

interface WeekData {
  week_start: string;
  week_label: string;
  total_tss: number;
  endurance_hours: number;
  tempo_hours: number;
  threshold_hours: number;
  vo2max_hours: number;
  anaerobic_hours: number;
}

type ChartRange = '30d' | '90d' | '6mo' | '1yr' | 'all';

const rangeOptions: { value: ChartRange; label: string }[] = [
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' },
  { value: '6mo', label: '6mo' },
  { value: '1yr', label: '1yr' },
  { value: 'all', label: 'All' },
];

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 12px', fontSize: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 4, color: '#333' }}>{label}</div>
      {payload.map((p: any) => (
        <div key={p.name} style={{ color: '#555', marginBottom: 2 }}>
          {p.name}: {typeof p.value === 'number' ? p.value.toFixed(1) : p.value}h
        </div>
      ))}
    </div>
  );
};

interface WeeklyLoadChartProps {
  targetUserId?: number | null;
}

const WeeklyLoadChart: React.FC<WeeklyLoadChartProps> = ({ targetUserId }) => {
  const [data, setData] = useState<WeekData[]>([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState<ChartRange>('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ range });
      if (targetUserId) params.set('target_user_id', String(targetUserId));
      const res = await fetch(`${API_BASE_URL}/api/analytics/weekly-load?${params}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to load weekly data');
      const json = await res.json();
      setData(json.weeks || []);
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [range, targetUserId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="analytics-card">
      <h3>Weekly Training Load</h3>
      <div className="analytics-range-buttons">
        {rangeOptions.map((opt) => (
          <button
            key={opt.value}
            className={`analytics-range-btn ${range === opt.value ? 'active' : ''}`}
            onClick={() => setRange(opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="analytics-chart-loading">Loading...</div>
      ) : data.length === 0 ? (
        <div className="analytics-chart-empty">No weekly load data available</div>
      ) : (
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="week_label"
              tick={{ fontSize: 11, fill: '#999' }}
              interval="preserveStartEnd"
            />
            <YAxis tick={{ fontSize: 11, fill: '#999' }} />
            <Tooltip content={<CustomTooltip />} isAnimationActive={false} />
            <Legend
              wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
              formatter={legendTooltipFormatter(weeklyLoadLegendDefs)}
            />
            <Bar dataKey="endurance_hours" stackId="load" fill="#3b82f6" name="Endurance" />
            <Bar dataKey="tempo_hours" stackId="load" fill="#22c55e" name="Tempo" />
            <Bar dataKey="threshold_hours" stackId="load" fill="#f59e0b" name="Threshold" />
            <Bar dataKey="vo2max_hours" stackId="load" fill="#ef4444" name="VO2max" />
            <Bar dataKey="anaerobic_hours" stackId="load" fill="#7c3aed" name="Anaerobic" />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default WeeklyLoadChart;
